import { processEnergyDataWithExtrapolation, ExtrapolationResult } from './energyExtrapolationService';
import { ELEC_COM_FORMAT, ELEC_RES_FORMAT, NG_COM_FORMAT, NG_RES_FORMAT } from './energyConsumptionSources';

export interface BuildingsEnergyData {
  electricity: {
    residential: ExtrapolationResult;
    commercial: ExtrapolationResult;
  };
  naturalGas: {
    residential: ExtrapolationResult;
    commercial: ExtrapolationResult;
  };
}

// Simple in-memory cache to prevent redundant API calls
const cache: Map<string, { data: BuildingsEnergyData; timestamp: number }> = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// In-memory overrides coming from the Energy & Buildings page data tables (per location).
// This lets other pages (GHG Inventory, Policy Tool) reflect user edits without writing to Supabase.
const overrides: Map<string, BuildingsEnergyData> = new Map();

export function setBuildingsEnergyDataOverride(location: string, data: Partial<BuildingsEnergyData>): void {
  const existing = overrides.get(location);
  const merged: BuildingsEnergyData = {
    electricity: {
      residential: data.electricity?.residential || existing?.electricity.residential || { years: [], values: [], sources: [], providers: [], dataSources: [] },
      commercial: data.electricity?.commercial || existing?.electricity.commercial || { years: [], values: [], sources: [], providers: [], dataSources: [] },
    },
    naturalGas: {
      residential: data.naturalGas?.residential || existing?.naturalGas.residential || { years: [], values: [], sources: [], providers: [], dataSources: [] },
      commercial: data.naturalGas?.commercial || existing?.naturalGas.commercial || { years: [], values: [], sources: [], providers: [], dataSources: [] },
    },
  };
  overrides.set(location, merged);
  // Also bust cache for this location so fresh calls recompute with overrides in mind.
  cache.delete(location);
}

export function clearBuildingsEnergyOverrides(location?: string): void {
  if (location) overrides.delete(location);
  else overrides.clear();
}

type EnergyTableRow = { year: number; residential: number; commercial: number };

export function setBuildingsEnergyOverrideFromEnergyPage(
  location: string,
  kind: 'electricity' | 'naturalGas',
  rows: EnergyTableRow[]
): void {
  // Policy tool needs projections through 2045.
  const years = Array.from({ length: 2045 - 2005 + 1 }, (_, i) => 2005 + i);
  const byYear = new Map(rows.map((r) => [r.year, r]));

  const res: ExtrapolationResult = {
    years,
    values: years.map((y) => (byYear.get(y)?.residential ?? null)),
    sources: years.map((y) => (byYear.has(y) ? 'observed' : 'extrapolated')),
    providers: years.map((y) => (byYear.has(y) ? 'Energy page (user)' : 'Energy page (missing)')),
    dataSources: years.map(() => null),
  };
  const com: ExtrapolationResult = {
    years,
    values: years.map((y) => (byYear.get(y)?.commercial ?? null)),
    sources: years.map((y) => (byYear.has(y) ? 'observed' : 'extrapolated')),
    providers: years.map((y) => (byYear.has(y) ? 'Energy page (user)' : 'Energy page (missing)')),
    dataSources: years.map(() => null),
  };

  if (kind === 'electricity') {
    setBuildingsEnergyDataOverride(location, { electricity: { residential: res, commercial: com } });
  } else {
    setBuildingsEnergyDataOverride(location, { naturalGas: { residential: res, commercial: com } });
  }
  
  // Clear baseline cache for this location when energy data changes
  import('./policyBaselineCache').then(({ clearBaselineCache }) => {
    clearBaselineCache(location);
  }).catch(() => {
    // Ignore errors if policyBaselineCache is not available (e.g., during initial load)
  });
}

/**
 * Clear the cache for a specific location or all locations
 */
export function clearBuildingsEnergyCache(location?: string): void {
  if (location) {
    cache.delete(location);
  } else {
    cache.clear();
  }
}

/**
 * Get processed electricity and natural gas data from Buildings page
 * This includes user customizations and extrapolation that's not in Supabase
 * Results are cached for 5 minutes to prevent redundant API calls
 */
export async function getBuildingsEnergyData(location: string): Promise<BuildingsEnergyData> {
  // Highest priority: user overrides from the Energy & Buildings page.
  const override = overrides.get(location);

  // Check cache first
  const cached = cache.get(location);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    console.log(`📦 Using cached Buildings energy data for ${location}`);
    // If we have an override, merge it on top of cached data
    if (override) {
      return {
        electricity: {
          residential: override.electricity.residential.years.length ? override.electricity.residential : cached.data.electricity.residential,
          commercial: override.electricity.commercial.years.length ? override.electricity.commercial : cached.data.electricity.commercial,
        },
        naturalGas: {
          residential: override.naturalGas.residential.years.length ? override.naturalGas.residential : cached.data.naturalGas.residential,
          commercial: override.naturalGas.commercial.years.length ? override.naturalGas.commercial : cached.data.naturalGas.commercial,
        },
      };
    }
    return cached.data;
  }

  // Policy tool needs projections through 2045. Using a shared range also ensures overrides apply cleanly.
  const years = Array.from({ length: 2045 - 2005 + 1 }, (_, i) => 2005 + i);
  
  try {
    // Fetch all energy data using the same extrapolation service as Buildings page
    // Add error handling for CORS issues
    const [elecRes, elecCom, gasRes, gasCom] = await Promise.all([
      processEnergyDataWithExtrapolation(location, ELEC_RES_FORMAT, 'electricity', years).catch(err => {
        console.error(`Error fetching electricity residential data for ${location}:`, err);
        return { years, values: years.map(() => null), sources: years.map(() => 'extrapolated' as const), providers: years.map(() => 'Error'), dataSources: years.map(() => null) };
      }),
      processEnergyDataWithExtrapolation(location, ELEC_COM_FORMAT, 'electricity', years).catch(err => {
        console.error(`Error fetching electricity commercial data for ${location}:`, err);
        return { years, values: years.map(() => null), sources: years.map(() => 'extrapolated' as const), providers: years.map(() => 'Error'), dataSources: years.map(() => null) };
      }),
      processEnergyDataWithExtrapolation(location, NG_RES_FORMAT, 'natural_gas', years).catch(err => {
        console.error(`Error fetching natural gas residential data for ${location}:`, err);
        // Check if it's a CORS error
        if (err instanceof TypeError && (err.message.includes('CORS') || err.message.includes('Failed to fetch'))) {
          console.error('🚨 CORS error detected. Note: Supabase REST API handles CORS automatically.');
          console.error('   This may indicate a network issue, proxy, or browser extension interfering.');
          console.error('   Check: Network tab in DevTools, VPN/proxy settings, browser extensions.');
        }
        return { years, values: years.map(() => null), sources: years.map(() => 'extrapolated' as const), providers: years.map(() => 'Error'), dataSources: years.map(() => null) };
      }),
      processEnergyDataWithExtrapolation(location, NG_COM_FORMAT, 'natural_gas', years).catch(err => {
        console.error(`Error fetching natural gas commercial data for ${location}:`, err);
        if (err instanceof TypeError && (err.message.includes('CORS') || err.message.includes('Failed to fetch'))) {
          console.error('🚨 CORS error detected. Note: Supabase REST API handles CORS automatically.');
          console.error('   This may indicate a network issue, proxy, or browser extension interfering.');
          console.error('   Check: Network tab in DevTools, VPN/proxy settings, browser extensions.');
        }
        return { years, values: years.map(() => null), sources: years.map(() => 'extrapolated' as const), providers: years.map(() => 'Error'), dataSources: years.map(() => null) };
      })
    ]);

    const result = {
      electricity: {
        residential: elecRes,
        commercial: elecCom
      },
      naturalGas: {
        residential: gasRes,
        commercial: gasCom
      }
    };

    const finalResult: BuildingsEnergyData = override
      ? {
          electricity: {
            residential: override.electricity.residential.years.length ? override.electricity.residential : result.electricity.residential,
            commercial: override.electricity.commercial.years.length ? override.electricity.commercial : result.electricity.commercial,
          },
          naturalGas: {
            residential: override.naturalGas.residential.years.length ? override.naturalGas.residential : result.naturalGas.residential,
            commercial: override.naturalGas.commercial.years.length ? override.naturalGas.commercial : result.naturalGas.commercial,
          },
        }
      : result;

    // Cache the result
    cache.set(location, { data: finalResult, timestamp: Date.now() });
    
    return finalResult;
  } catch (error) {
    console.error(`Error fetching Buildings energy data for ${location}:`, error);
    throw error;
  }
}

/**
 * Calculate total electricity consumption (kWh) for GHG inventory
 * Uses processed data from Buildings page with extrapolation
 */
export function calculateTotalElectricityConsumption(
  buildingsData: BuildingsEnergyData,
  year: number
): number {
  const yearIndex = buildingsData.electricity.residential.years.indexOf(year);
  if (yearIndex === -1) return 0;
  
  const resKwh = buildingsData.electricity.residential.values[yearIndex] || 0;
  const comKwh = buildingsData.electricity.commercial.values[yearIndex] || 0;
  
  return resKwh + comKwh;
}

/**
 * Calculate total natural gas consumption (therms) for GHG inventory
 * Uses processed data from Buildings page with extrapolation
 */
export function calculateTotalNaturalGasConsumption(
  buildingsData: BuildingsEnergyData | null | undefined,
  year: number
): number {
  const { resTherms, comTherms } = getNaturalGasConsumptionBySector(buildingsData, year);
  return resTherms + comTherms;
}

/**
 * Get residential and commercial natural gas consumption (therms) by year.
 * Used by building electrification cost layer for res/com split.
 * For years not in data, uses last known value (projection years 2024+).
 */
export function getNaturalGasConsumptionBySector(
  buildingsData: BuildingsEnergyData | null | undefined,
  year: number
): { resTherms: number; comTherms: number } {
  if (!buildingsData?.naturalGas?.residential || !buildingsData?.naturalGas?.commercial) {
    return { resTherms: 0, comTherms: 0 };
  }
  const resYears = buildingsData.naturalGas.residential.years ?? [];
  const resVals = buildingsData.naturalGas.residential.values ?? [];
  const comYears = buildingsData.naturalGas.commercial.years ?? [];
  const comVals = buildingsData.naturalGas.commercial.values ?? [];
  const resIdx = resYears.indexOf(year);
  const comIdx = comYears.indexOf(year);
  let resTherms = resIdx >= 0 ? (resVals[resIdx] ?? 0) : 0;
  let comTherms = comIdx >= 0 ? (comVals[comIdx] ?? 0) : 0;
  if (resIdx < 0 && resYears.length > 0) {
    const lastIdx = resYears.length - 1;
    const lastYear = resYears[lastIdx];
    resTherms = year >= lastYear ? (resVals[lastIdx] ?? 0) : (resVals[0] ?? 0);
  }
  if (comIdx < 0 && comYears.length > 0) {
    const lastIdx = comYears.length - 1;
    const lastYear = comYears[lastIdx];
    comTherms = year >= lastYear ? (comVals[lastIdx] ?? 0) : (comVals[0] ?? 0);
  }
  return { resTherms, comTherms };
}

/**
 * Residential and commercial electricity (kWh) by year.
 * Mirrors getNaturalGasConsumptionBySector for location-specific intensity.
 */
export function getElectricityConsumptionBySector(
  buildingsData: BuildingsEnergyData | null | undefined,
  year: number
): { resKwh: number; comKwh: number } {
  if (!buildingsData?.electricity?.residential || !buildingsData?.electricity?.commercial) {
    return { resKwh: 0, comKwh: 0 };
  }
  const resYears = buildingsData.electricity.residential.years ?? [];
  const resVals = buildingsData.electricity.residential.values ?? [];
  const comYears = buildingsData.electricity.commercial.years ?? [];
  const comVals = buildingsData.electricity.commercial.values ?? [];
  const resIdx = resYears.indexOf(year);
  const comIdx = comYears.indexOf(year);
  let resKwh = resIdx >= 0 ? Number(resVals[resIdx] ?? 0) : 0;
  let comKwh = comIdx >= 0 ? Number(comVals[comIdx] ?? 0) : 0;
  if (resIdx < 0 && resYears.length > 0) {
    const lastIdx = resYears.length - 1;
    const lastYear = resYears[lastIdx];
    resKwh = year >= lastYear ? Number(resVals[lastIdx] ?? 0) : Number(resVals[0] ?? 0);
  }
  if (comIdx < 0 && comYears.length > 0) {
    const lastIdx = comYears.length - 1;
    const lastYear = comYears[lastIdx];
    comKwh = year >= lastYear ? Number(comVals[lastIdx] ?? 0) : Number(comVals[0] ?? 0);
  }
  return { resKwh, comKwh };
}

/**
 * Calculate electricity emissions using processed Buildings data
 * Applies emission factor to kWh consumption
 */
export function calculateElectricityEmissions(
  buildingsData: BuildingsEnergyData,
  year: number,
  emissionFactorKgCO2PerKwh: number = 0.0003 // Default CA grid emission factor (kg CO2/kWh)
): number {
  const totalKwh = calculateTotalElectricityConsumption(buildingsData, year);
  // Convert kg CO2 to tons CO2e
  return (totalKwh * emissionFactorKgCO2PerKwh) / 1000;
}

/**
 * Calculate natural gas emissions using processed Buildings data
 * Applies emission factor to therm consumption
 */
export function calculateNaturalGasEmissions(
  buildingsData: BuildingsEnergyData,
  year: number,
  emissionFactorKgCO2PerTherm: number = 5.29 // EPA's Greenhouse Gas Equivalencies Calculator: 5.29 kg CO2e/therm
): number {
  const totalTherms = calculateTotalNaturalGasConsumption(buildingsData, year);
  // Convert kg CO2e to metric tons CO2e
  return (totalTherms * emissionFactorKgCO2PerTherm) / 1000;
}


